import * as React from "react"
import type { DateRange } from "react-day-picker"

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../select/select.js"
import { Separator } from "../separator/separator.js"
import { MonthGrid } from "./month-grid.js"
import {
  buildMonthRangeFromParts,
  getMonthYearBounds,
  isMonthRangeOrderInvalid,
  isMonthYearOutOfBounds,
  listYears,
  MONTH_OPTIONS,
} from "./month-range.js"

type MonthRangePickerPanelLabels = {
  from?: string
  to?: string
  month?: string
  year?: string
  invalidRange?: string
  previousYear?: string
  nextYear?: string
}

type MonthRangePickerPanelProps = {
  value?: DateRange
  onChange: (range: DateRange | undefined) => void
  onValidityChange?: (isValid: boolean) => void
  startMonth: Date
  endMonth: Date
  fromDate?: Date
  toDate?: Date
  labels?: MonthRangePickerPanelLabels
}

type MonthYearParts = {
  month: number | undefined
  year: number | undefined
}

function partsFromDate(date: Date | undefined): MonthYearParts {
  if (!date) return { month: undefined, year: undefined }
  return { month: date.getMonth(), year: date.getFullYear() }
}

function MonthYearSelectRow({
  label,
  month,
  year,
  years,
  monthPlaceholder,
  yearPlaceholder,
  invalid,
  onMonthChange,
  onYearChange,
}: {
  label: string
  month: number | undefined
  year: number | undefined
  years: number[]
  monthPlaceholder: string
  yearPlaceholder: string
  invalid?: boolean
  onMonthChange: (month: number) => void
  onYearChange: (year: number) => void
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      <div className="grid grid-cols-2 gap-2">
        <Select
          value={month === undefined ? undefined : String(month)}
          onValueChange={(next) => onMonthChange(Number(next))}
        >
          <SelectTrigger aria-label={`${label} ${monthPlaceholder}`} aria-invalid={invalid}>
            <SelectValue placeholder={monthPlaceholder} />
          </SelectTrigger>
          <SelectContent>
            {MONTH_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={String(option.value)}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={year === undefined ? undefined : String(year)}
          onValueChange={(next) => onYearChange(Number(next))}
        >
          <SelectTrigger aria-label={`${label} ${yearPlaceholder}`} aria-invalid={invalid}>
            <SelectValue placeholder={yearPlaceholder} />
          </SelectTrigger>
          <SelectContent>
            {years.map((optionYear) => (
              <SelectItem key={optionYear} value={String(optionYear)}>
                {optionYear}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  )
}

function isComplete(parts: MonthYearParts): parts is { month: number; year: number } {
  return parts.month !== undefined && parts.year !== undefined
}

function MonthRangePickerPanel({
  value,
  onChange,
  onValidityChange,
  startMonth,
  endMonth,
  fromDate,
  toDate,
  labels,
}: MonthRangePickerPanelProps) {
  const years = React.useMemo(() => listYears(startMonth, endMonth), [startMonth, endMonth])
  const monthYearBounds = React.useMemo(
    () => getMonthYearBounds({ fromDate, toDate }),
    [fromDate, toDate],
  )
  const [fromParts, setFromParts] = React.useState<MonthYearParts>(() => partsFromDate(value?.from))
  const [toParts, setToParts] = React.useState<MonthYearParts>(() => partsFromDate(value?.to))

  React.useEffect(() => {
    setFromParts(partsFromDate(value?.from))
    setToParts(partsFromDate(value?.to))
  }, [value?.from?.getTime(), value?.to?.getTime()])

  const monthPlaceholder = labels?.month ?? "Month"
  const yearPlaceholder = labels?.year ?? "Year"
  const fromComplete = isComplete(fromParts)
  const toComplete = isComplete(toParts)
  const fromOutOfBounds =
    fromComplete && isMonthYearOutOfBounds(fromParts.month, fromParts.year, monthYearBounds)
  const toOutOfBounds =
    toComplete && isMonthYearOutOfBounds(toParts.month, toParts.year, monthYearBounds)
  const rangeOrderInvalid =
    fromComplete &&
    toComplete &&
    isMonthRangeOrderInvalid(
      fromParts.month,
      fromParts.year,
      toParts.month,
      toParts.year,
    )
  const isPanelValid =
    !fromOutOfBounds && !toOutOfBounds && !rangeOrderInvalid && (!fromComplete || toComplete)

  React.useEffect(() => {
    onValidityChange?.(isPanelValid)
  }, [isPanelValid, onValidityChange])

  function emitRange(nextFrom: MonthYearParts, nextTo: MonthYearParts) {
    const nextFromComplete = isComplete(nextFrom)
    const nextToComplete = isComplete(nextTo)

    if (!nextFromComplete) {
      onChange(undefined)
      return
    }

    if (isMonthYearOutOfBounds(nextFrom.month, nextFrom.year, monthYearBounds)) {
      return
    }

    if (!nextToComplete) {
      onChange({
        from: buildMonthRangeFromParts(
          nextFrom.month,
          nextFrom.year,
          nextFrom.month,
          nextFrom.year,
        ).from,
      })
      return
    }

    if (
      isMonthYearOutOfBounds(nextTo.month, nextTo.year, monthYearBounds) ||
      isMonthRangeOrderInvalid(
        nextFrom.month,
        nextFrom.year,
        nextTo.month,
        nextTo.year,
      )
    ) {
      return
    }

    onChange(
      buildMonthRangeFromParts(
        nextFrom.month,
        nextFrom.year,
        nextTo.month,
        nextTo.year,
      ),
    )
  }

  function handleFromMonthChange(month: number) {
    const nextFrom = { ...fromParts, month }
    setFromParts(nextFrom)
    emitRange(nextFrom, toParts)
  }

  function handleFromYearChange(year: number) {
    const nextFrom = { ...fromParts, year }
    setFromParts(nextFrom)
    emitRange(nextFrom, toParts)
  }

  function handleToMonthChange(month: number) {
    const nextTo = { ...toParts, month }
    setToParts(nextTo)
    emitRange(fromParts, nextTo)
  }

  function handleToYearChange(year: number) {
    const nextTo = { ...toParts, year }
    setToParts(nextTo)
    emitRange(fromParts, nextTo)
  }

  return (
    <div className="flex w-[min(100vw-2rem,20rem)] flex-col gap-3 p-3">
      <MonthGrid
        value={value}
        onChange={onChange}
        startMonth={startMonth}
        endMonth={endMonth}
        fromDate={fromDate}
        toDate={toDate}
        labels={{ previousYear: labels?.previousYear, nextYear: labels?.nextYear }}
      />
      <Separator />
      <MonthYearSelectRow
        label={labels?.from ?? "From"}
        month={fromParts.month}
        year={fromParts.year}
        years={years}
        monthPlaceholder={monthPlaceholder}
        yearPlaceholder={yearPlaceholder}
        invalid={fromOutOfBounds}
        onMonthChange={handleFromMonthChange}
        onYearChange={handleFromYearChange}
      />
      <MonthYearSelectRow
        label={labels?.to ?? "To"}
        month={toParts.month}
        year={toParts.year}
        years={years}
        monthPlaceholder={monthPlaceholder}
        yearPlaceholder={yearPlaceholder}
        invalid={toOutOfBounds || rangeOrderInvalid}
        onMonthChange={handleToMonthChange}
        onYearChange={handleToYearChange}
      />
      {rangeOrderInvalid ? (
        <p role="alert" className="text-xs text-error">
          {labels?.invalidRange ?? "End month must be on or after start month"}
        </p>
      ) : null}
    </div>
  )
}

export { MonthRangePickerPanel, type MonthRangePickerPanelLabels }
